import {
  Add,
  Analytics,
  Delete,
  Download,
  ExpandMore,
  Refresh,
  TrendingUp,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import { SpareForecastItem } from "../../../@types/DRO/GenerateSpareForecastResponse";
import { GenerateSpareForecastBody } from "../../../@types/DTO/GenerateSpareForecastBody";
import { SpareForecastApi } from "../../../api/SpareForecast/SpareForecastApi";
import { useLoading } from "../../../views/Loader/LoadingContext";

const SpareForecast: React.FC = () => {
  const { setLoading } = useLoading();
  const [formData, setFormData] = useState<GenerateSpareForecastBody>({
    n_days: 90,
    n_days_bins: 30,
    config: {
      cities: {
        MUM: 2.5,
        NCR: 2,
        BLR: 2.5,
        JAI: 2.5,
        HYD: 2.5,
      },
      tyre: 1.5,
      oem: {},
      part_category_class: {},
      stockout_buffer_applicable: [],
      min_transfer_cost_threshold: 100000,
      transfer_order_stock_buffer_multiplier: 1,
    },
  });

  const [forecastResults, setForecastResults] = useState<SpareForecastItem[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    cities: true,
    advanced: false,
  });

  const [expandedAdvanced, setExpandedAdvanced] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState(true);

  // Dropdown data + state
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [dropdownError, setDropdownError] = useState<string | null>(null);
  const [dropdownCities, setDropdownCities] = useState<string[]>([]);
  const [dropdownOems, setDropdownOems] = useState<string[]>([]);
  const [dropdownCategories, setDropdownCategories] = useState<string[]>([]);
  const [dropdownStockoutParts, setDropdownStockoutParts] = useState<number[]>(
    []
  );

  // City input state (dropdown)
  const [selectedCityCode, setSelectedCityCode] = useState<string | null>(null);
  const [newCityMultiplier, setNewCityMultiplier] = useState("");

  // OEM input state (dropdown)
  const [selectedOemId, setSelectedOemId] = useState<string | null>(null);
  const [newOemMultiplier, setNewOemMultiplier] = useState("");

  // Category class input state (dropdown)
  const [selectedCategoryClass, setSelectedCategoryClass] = useState<
    string | null
  >(null);
  const [newCategoryMultiplier, setNewCategoryMultiplier] = useState("");

  const handleInputChange = (
    field: keyof GenerateSpareForecastBody,
    value: number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConfigChange = (
    field: keyof GenerateSpareForecastBody["config"],
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value,
      },
    }));
  };

  const fetchDropdowns = async () => {
    setDropdownLoading(true);
    setDropdownError(null);
    try {
      const [cities, categories, oems, stockoutParts] = await Promise.all([
        SpareForecastApi.api.getSparesCities(),
        SpareForecastApi.api.getPartCategories(),
        SpareForecastApi.api.getSparesOem(),
        SpareForecastApi.api.getStockoutParts(),
      ]);

      // Extract city codes from format like "Bangalore : BLR"
      setDropdownCities(
        (Array.isArray(cities) ? cities : [])
          .filter(Boolean)
          .map((c) => {
            const parts = String(c).split(":");
            return parts.length > 1 ? parts[1].trim() : String(c).toUpperCase();
          })
      );
      setDropdownCategories(
        (Array.isArray(categories) ? categories : [])
          .filter(Boolean)
          .map((c) => String(c).toUpperCase())
      );
      setDropdownOems(
        (Array.isArray(oems) ? oems : []).filter(Boolean).map(String)
      );
      setDropdownStockoutParts(
        (Array.isArray(stockoutParts) ? stockoutParts : [])
          .map((x) => Number(x))
          .filter((x) => !Number.isNaN(x))
      );
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load dropdowns.";
      setDropdownError(msg);
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDropdownLoading(false);
    }
  };

  useEffect(() => {
    fetchDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddCity = () => {
    if (selectedCityCode && newCityMultiplier) {
      const multiplier = parseFloat(newCityMultiplier);
      if (!isNaN(multiplier)) {
        handleConfigChange("cities", {
          ...formData.config.cities,
          [selectedCityCode.toUpperCase()]: multiplier,
        });
        setSelectedCityCode(null);
        setNewCityMultiplier("");
      }
    }
  };

  const handleRemoveCity = (cityCode: string) => {
    const newCities = { ...formData.config.cities };
    delete newCities[cityCode];
    handleConfigChange("cities", newCities);
  };

  const handleAddOem = () => {
    if (selectedOemId && newOemMultiplier) {
      const multiplier = parseFloat(newOemMultiplier);
      if (!isNaN(multiplier)) {
        handleConfigChange("oem", {
          ...(formData.config.oem || {}),
          [selectedOemId]: multiplier,
        });
        setSelectedOemId(null);
        setNewOemMultiplier("");
      }
    }
  };

  const handleRemoveOem = (oemId: string) => {
    const newOem = { ...(formData.config.oem || {}) };
    delete newOem[oemId];
    handleConfigChange("oem", newOem);
  };

  const handleAddCategoryClass = () => {
    if (selectedCategoryClass && newCategoryMultiplier) {
      const multiplier = parseFloat(newCategoryMultiplier);
      if (!isNaN(multiplier)) {
        handleConfigChange("part_category_class", {
          ...(formData.config.part_category_class || {}),
          [selectedCategoryClass.toUpperCase()]: multiplier,
        });
        setSelectedCategoryClass(null);
        setNewCategoryMultiplier("");
      }
    }
  };

  const handleRemoveCategoryClass = (categoryClass: string) => {
    const newCategory = { ...(formData.config.part_category_class || {}) };
    delete newCategory[categoryClass];
    handleConfigChange("part_category_class", newCategory);
  };

  const handleRemovePartId = (partId: number) => {
    const currentList = formData.config.stockout_buffer_applicable || [];
    handleConfigChange(
      "stockout_buffer_applicable",
      currentList.filter((id) => id !== partId)
    );
  };

  const handleGenerateForecast = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await SpareForecastApi.api.generateSpareForecast(
        formData
      );
      if (response.data && Array.isArray(response.data)) {
        setForecastResults(response.data);
        setSnackbarMessage(
          `Successfully generated forecast for ${response.data.length} parts`
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to generate forecast. Please try again.";
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setForecastResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = () => {
    if (forecastResults.length === 0) {
      setSnackbarMessage("No data to download");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Convert to CSV
    const headers = [
      "City",
      "Part ID",
      "Part Name",
      "Reference Code",
      "Required Quantity",
      "Current Stock",
      "Order Quantity",
      "Order Cost",
      "Transfer Order Applicable",
      "Source City",
      "Transfer Quantity",
    ];

    const csvRows = [
      headers.join(","),
      ...forecastResults.map((item) =>
        [
          item.city,
          item.final_part_id,
          `"${item.part_name}"`,
          item.reference_code,
          item.required_quantity_final,
          item.quantity,
          item.order_quantity,
          item.order_cost,
          item.transfer_order_applicable ? "Yes" : "No",
          item.source_city || "",
          item.transfer_qty || "",
        ].join(",")
      ),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `spare-forecast-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    setSnackbarMessage("Report downloaded successfully");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 2,
    }).format(value);
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const cityOptions = useMemo(() => dropdownCities, [dropdownCities]);
  const oemOptions = useMemo(() => dropdownOems, [dropdownOems]);
  const categoryOptions = useMemo(
    () => dropdownCategories,
    [dropdownCategories]
  );
  const stockoutOptions = useMemo(
    () => dropdownStockoutParts,
    [dropdownStockoutParts]
  );

  return (
    <Box sx={{ p: 2.5, bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#171F21",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <Analytics sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#171F21" }}>
                Spare Forecast Generator
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                Generate demand forecasts and procurement recommendations
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Refresh dropdowns">
            <span>
              <IconButton
                onClick={fetchDropdowns}
                disabled={dropdownLoading}
                size="small"
                sx={{
                  border: "1px solid rgba(23,31,33,0.12)",
                  borderRadius: 1.5,
                  "&:hover": { bgcolor: "rgba(23,31,33,0.04)" },
                }}>
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </motion.div>

      {/* Filter Panel */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}>
          {/* Filter Header with Collapse */}
          <Box
            sx={{
              p: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "rgba(0,0,0,0.01)",
              borderBottom: expandedFilters ? "1px solid rgba(0,0,0,0.06)" : "none",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
            }}
            onClick={() => setExpandedFilters(!expandedFilters)}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                }}>
                <TrendingUp sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#171F21" }}>
                  Forecast Parameters
                </Typography>
                <Typography variant="caption" sx={{ color: "#6b7280" }}>
                  {Object.keys(formData.config.cities).length} cities • Advanced settings available
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              sx={{
                transform: expandedFilters ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.3s ease",
                color: "#171F21",
              }}>
              <ExpandMore />
            </IconButton>
          </Box>

          {/* Filter Content */}
          <Collapse in={expandedFilters} timeout="auto" unmountOnExit>
            <Box sx={{ p: 3 }}>
              {dropdownError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {dropdownError}
                </Alert>
              )}

              {/* Basic Parameters - Grid Layout */}
              <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Historical Days"
                    type="number"
                    value={formData.n_days}
                    onChange={(e) =>
                      handleInputChange("n_days", parseInt(e.target.value))
                    }
                    size="small"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Bin Size (Days)"
                    type="number"
                    value={formData.n_days_bins}
                    onChange={(e) =>
                      handleInputChange("n_days_bins", parseInt(e.target.value))
                    }
                    size="small"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Tyre Multiplier"
                    type="number"
                    value={formData.config.tyre}
                    onChange={(e) =>
                      handleConfigChange("tyre", parseFloat(e.target.value))
                    }
                    size="small"
                    variant="outlined"
                    inputProps={{ step: 0.1 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<TrendingUp />}
                    onClick={handleGenerateForecast}
                    sx={{
                      py: 1.2,
                      bgcolor: "#171F21",
                      "&:hover": { bgcolor: "#374151" },
                      textTransform: "none",
                      borderRadius: 1.5,
                      fontWeight: 600,
                    }}>
                    Generate
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* City Multipliers Section */}
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "#171F21" }}>
                  City Multipliers
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  {Object.entries(formData.config.cities).map(([code, multiplier]) => (
                    <Chip
                      key={code}
                      label={`${code}: ${multiplier}`}
                      onDelete={() => handleRemoveCity(code)}
                      sx={{ mr: 1, mb: 1, fontWeight: 500 }}
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Autocomplete
                    options={cityOptions}
                    value={selectedCityCode}
                    onChange={(_, v) => setSelectedCityCode(v)}
                    loading={dropdownLoading}
                    size="small"
                    sx={{ flex: 1, minWidth: 140 }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="City" size="small" />
                    )}
                  />
                  <TextField
                    placeholder="Multiplier"
                    type="number"
                    value={newCityMultiplier}
                    onChange={(e) => setNewCityMultiplier(e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                    inputProps={{ step: 0.1 }}
                  />
                  <IconButton
                    onClick={handleAddCity}
                    sx={{
                      bgcolor: "#171F21",
                      color: "white",
                      borderRadius: 1.5,
                      "&:hover": { bgcolor: "#374151" },
                    }}
                    size="small">
                    <Add fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Advanced Settings Accordion */}
              <Accordion
                expanded={expandedAdvanced}
                onChange={() => setExpandedAdvanced(!expandedAdvanced)}
                sx={{
                  boxShadow: "none",
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 1.5,
                  "&:before": { display: "none" },
                }}>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    py: 1,
                    px: 2,
                    bgcolor: "rgba(0,0,0,0.02)",
                    borderRadius: "12px 12px 0 0",
                  }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Advanced Settings
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  <Grid container spacing={2.5}>
                    {/* Transfer Cost Threshold & Buffer Multiplier */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Min Transfer Cost Threshold"
                        type="number"
                        value={formData.config.min_transfer_cost_threshold}
                        onChange={(e) =>
                          handleConfigChange(
                            "min_transfer_cost_threshold",
                            parseFloat(e.target.value)
                          )
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Transfer Order Stock Buffer Multiplier"
                        type="number"
                        value={
                          formData.config.transfer_order_stock_buffer_multiplier
                        }
                        onChange={(e) =>
                          handleConfigChange(
                            "transfer_order_stock_buffer_multiplier",
                            parseFloat(e.target.value)
                          )
                        }
                        size="small"
                        variant="outlined"
                        inputProps={{ step: 0.1 }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                          },
                        }}
                      />
                    </Grid>

                    {/* OEM Multipliers */}
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, display: "block", mb: 1.5, color: "#171F21" }}>
                        OEM Multipliers
                      </Typography>
                      <Box sx={{ mb: 1.5 }}>
                        {Object.entries(formData.config.oem || {}).map(
                          ([id, multiplier]) => (
                            <Chip
                              key={id}
                              label={`OEM ${id}: ${multiplier}`}
                              onDelete={() => handleRemoveOem(id)}
                              sx={{ mr: 1, mb: 1, fontWeight: 500 }}
                              size="small"
                              variant="outlined"
                            />
                          )
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Autocomplete
                          options={oemOptions}
                          value={selectedOemId}
                          onChange={(_, v) => setSelectedOemId(v)}
                          loading={dropdownLoading}
                          size="small"
                          sx={{ flex: 1, minWidth: 140 }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="OEM"
                              size="small"
                            />
                          )}
                        />
                        <TextField
                          placeholder="Multiplier"
                          type="number"
                          value={newOemMultiplier}
                          onChange={(e) => setNewOemMultiplier(e.target.value)}
                          size="small"
                          sx={{ width: 120 }}
                          inputProps={{ step: 0.1 }}
                        />
                        <IconButton
                          onClick={handleAddOem}
                          sx={{
                            bgcolor: "#171F21",
                            color: "white",
                            borderRadius: 1.5,
                            "&:hover": { bgcolor: "#374151" },
                          }}
                          size="small">
                          <Add fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>

                    {/* Category Class Multipliers */}
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, display: "block", mb: 1.5, color: "#171F21" }}>
                        Category Class Multipliers
                      </Typography>
                      <Box sx={{ mb: 1.5 }}>
                        {Object.entries(
                          formData.config.part_category_class || {}
                        ).map(([category, multiplier]) => (
                          <Chip
                            key={category}
                            label={`${category}: ${multiplier}`}
                            onDelete={() =>
                              handleRemoveCategoryClass(category)
                            }
                            sx={{ mr: 1, mb: 1, fontWeight: 500 }}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                      <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Autocomplete
                          options={categoryOptions}
                          value={selectedCategoryClass}
                          onChange={(_, v) => setSelectedCategoryClass(v)}
                          loading={dropdownLoading}
                          size="small"
                          sx={{ flex: 1, minWidth: 160 }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Category"
                              size="small"
                            />
                          )}
                        />
                        <TextField
                          placeholder="Multiplier"
                          type="number"
                          value={newCategoryMultiplier}
                          onChange={(e) =>
                            setNewCategoryMultiplier(e.target.value)
                          }
                          size="small"
                          sx={{ width: 120 }}
                          inputProps={{ step: 0.1 }}
                        />
                        <IconButton
                          onClick={handleAddCategoryClass}
                          sx={{
                            bgcolor: "#171F21",
                            color: "white",
                            borderRadius: 1.5,
                            "&:hover": { bgcolor: "#374151" },
                          }}
                          size="small">
                          <Add fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>

                    {/* Stockout Buffer Applicable */}
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, display: "block", mb: 1.5, color: "#171F21" }}>
                        Stockout Buffer Exclusions (Part IDs)
                      </Typography>
                      <Autocomplete
                        multiple
                        options={stockoutOptions}
                        value={
                          formData.config.stockout_buffer_applicable || []
                        }
                        onChange={(_, values) =>
                          handleConfigChange(
                            "stockout_buffer_applicable",
                            values
                          )
                        }
                        loading={dropdownLoading}
                        size="small"
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option}
                              label={`Part ${option}`}
                              size="small"
                              onDelete={() => handleRemovePartId(option)}
                              deleteIcon={<Delete />}
                              variant="outlined"
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select stockout part IDs"
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Collapse>
        </Card>
      </motion.div>

      {/* Results Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}>
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}>
          <Box
            sx={{
              p: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid rgba(0,0,0,0.06)",
              bgcolor: "rgba(0,0,0,0.01)",
            }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#171F21" }}>
              Forecast Results
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              {forecastResults.length > 0 && (
                <>
                  <Chip
                    label={`${forecastResults.length} Parts`}
                    sx={{
                      bgcolor: "#e3f2fd",
                      color: "#1976d2",
                      fontWeight: 600,
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleDownloadReport}
                    size="small"
                    sx={{
                      textTransform: "none",
                      borderColor: "#171F21",
                      color: "#171F21",
                      borderRadius: 1.5,
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "#374151",
                        backgroundColor: "rgba(23, 31, 33, 0.04)",
                      },
                    }}>
                    Download Report
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {forecastResults.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 12,
                  color: "#9ca3af",
                }}>
                <Analytics sx={{ fontSize: 64, mb: 2, opacity: 0.2 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  No Forecast Data
                </Typography>
                <Typography variant="body2">
                  Configure parameters and click "Generate" to see results
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ borderRadius: 1.5, border: "1px solid rgba(0,0,0,0.06)" }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f9fafb" }}>
                      <TableCell sx={{ fontWeight: 700, color: "#171F21" }}>
                        City
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#171F21" }}>
                        Part Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#171F21" }}>
                        Ref Code
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#171F21" }}
                        align="right">
                        Required Qty
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#171F21" }}
                        align="right">
                        Current Stock
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#171F21" }}
                        align="right">
                        Order Qty
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#171F21" }}
                        align="right">
                        Order Cost
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#171F21" }}
                        align="center">
                        Transfer
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {forecastResults
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((item, index) => (
                        <TableRow
                          key={`${item.final_part_id}-${item.city}-${index}`}
                          hover
                          sx={{
                            "&:nth-of-type(odd)": { bgcolor: "#fafafa" },
                            "&:hover": { bgcolor: "#f0f9ff" },
                          }}>
                          <TableCell>
                            <Chip
                              label={item.city}
                              size="small"
                              sx={{
                                bgcolor: "#e3f2fd",
                                color: "#1976d2",
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={item.part_name}>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 200,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}>
                                {item.part_name}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.reference_code}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatNumber(item.required_quantity_final)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatNumber(item.quantity)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color:
                                  item.order_quantity > 0
                                    ? "#1976d2"
                                    : "inherit",
                              }}>
                              {formatNumber(item.order_quantity)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#d32f2f",
                              }}>
                              {formatCurrency(item.order_cost)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {item.transfer_order_applicable ? (
                              <Tooltip
                                title={`From ${item.source_city}: ${item.transfer_qty} units`}>
                                <Chip
                                  label="Yes"
                                  size="small"
                                  sx={{
                                    bgcolor: "#c8e6c9",
                                    color: "#2e7d32",
                                    fontWeight: 600,
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Chip
                                label="No"
                                size="small"
                                sx={{
                                  bgcolor: "#ffcdd2",
                                  color: "#c62828",
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={forecastResults.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{
                    borderTop: "1px solid rgba(0,0,0,0.06)",
                  }}
                />
              </TableContainer>
            )}
          </Box>
        </Card>
      </motion.div>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SpareForecast;
